import React, { useState, useRef } from "react";
import "../styles/Drones.css";
import { useQuery, useMutation, gql } from "@apollo/client";

const ITEMS_PER_PAGE = 10;
const PRODUCTS_PER_PAGE = 5;

// GraphQL Definitions
const GET_RENTALS = gql`
  query {
    rentals {
      rentalId
      name
      brand
      location
      pricePerHour
      pricePerDay
      description
      quantity
      image
      status
      insurance
      with_pilot
      available_today
      sellerId
      sellerInfo {
        email
        phoneNumber
      }
    }
  }
`;

const UPDATE_RENTAL_STATUS = gql`
  mutation UpdateRentalStatus($rentalId: String!, $status: String!) {
    updateRentalStatus(rentalId: $rentalId, status: $status) {
      rentalId
      status
    }
  }
`;

const UPDATE_RENTAL = gql`
  mutation UpdateRental(
    $rentalId: String!
    $name: String!
    $brand: String!
    $location: String!
    $pricePerHour: Float!
    $pricePerDay: Float!
    $quantity: Int!
    $description: String!
    $insurance: Boolean!
    $with_pilot: Boolean!
    $available_today: Boolean!
  ) {
    updateRental(
      rentalId: $rentalId
      name: $name
      brand: $brand
      location: $location
      pricePerHour: $pricePerHour
      pricePerDay: $pricePerDay
      quantity: $quantity
      description: $description
      insurance: $insurance
      with_pilot: $with_pilot
      available_today: $available_today
    ) {
      rentalId
      name
      brand
      location
      pricePerHour
      pricePerDay
      quantity
      description
      insurance
      with_pilot
      available_today
    }
  }
`;

const DELETE_RENTAL = gql`
  mutation DeleteRental($rentalId: String!) {
    deleteRental(rentalId: $rentalId) {
      rentalId
    }
  }
`;

function Rentals() {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "sellerId", direction: "asc" });
  const [viewingRental, setViewingRental] = useState(null);
  const [viewingDescription, setViewingDescription] = useState(null);
  const [editingRental, setEditingRental] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPage, setProductsPage] = useState({});
  const tableRef = useRef(null);

  // Apollo Hooks
  const { loading, error: queryError, data, refetch } = useQuery(GET_RENTALS, {
    fetchPolicy: "network-only",
  });

  const [updateStatusMutation] = useMutation(UPDATE_RENTAL_STATUS);
  const [updateRentalMutation] = useMutation(UPDATE_RENTAL);
  const [deleteRentalMutation] = useMutation(DELETE_RENTAL);

  const rentals = data?.rentals || [];
  const error = queryError ? queryError.message : null;

  // Group rentals by seller ID
  const groupedBySeller = rentals.reduce((acc, rental) => {
    const sellerId = rental.sellerId;
    if (!acc[sellerId]) {
      acc[sellerId] = {
        sellerId: sellerId,
        sellerInfo: rental.sellerInfo,
        rentals: []
      };
    }
    acc[sellerId].rentals.push(rental);
    return acc;
  }, {});

  // Convert to array and sort
  const sellerGroups = Object.values(groupedBySeller).sort((a, b) =>
    a.sellerId.localeCompare(b.sellerId)
  );

  // Calculate seller stats
  const sellerGroupsWithStats = sellerGroups.map(seller => {
    const totalValue = seller.rentals.reduce((sum, r) => sum + (r.pricePerHour * r.quantity), 0);
    const totalStock = seller.rentals.reduce((sum, r) => sum + r.quantity, 0);
    const approvedRentals = seller.rentals.filter(r => r.status === 'approved').length;
    const pendingRentals = seller.rentals.filter(r => r.status === 'pending').length;
    const rejectedRentals = seller.rentals.filter(r => r.status === 'rejected').length;
    const deactivatedRentals = seller.rentals.filter(r => r.status === 'Temporarily_Deactivated').length;
    const insuredCount = seller.rentals.filter(r => r.insurance).length;
    const withPilotCount = seller.rentals.filter(r => r.with_pilot).length;
    const availableTodayCount = seller.rentals.filter(r => r.available_today).length;

    return {
      ...seller,
      totalValue,
      totalStock,
      approvedRentals,
      pendingRentals,
      rejectedRentals,
      deactivatedRentals,
      insuredCount,
      withPilotCount,
      availableTodayCount,
      totalRentals: seller.rentals.length
    };
  });

  // Filter sellers based on search
  const filteredSellers = sellerGroupsWithStats.filter(seller => {
    const matchesSearch =
      searchTerm === "" ||
      seller.sellerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.phoneNumber?.includes(searchTerm);

    if (selectedStatus === "all") return matchesSearch;

    // Filter by status
    const filteredRentals = seller.rentals.filter(rental =>
      rental.status.toLowerCase() === selectedStatus.toLowerCase()
    );

    return matchesSearch && filteredRentals.length > 0;
  });

  // Sort sellers
  const sortedSellers = [...filteredSellers].sort((a, b) => {
    const key = sortConfig.key;
    let aVal = a[key];
    let bVal = b[key];

    if (key === 'sellerId' || key === 'email') {
      aVal = aVal?.toLowerCase?.() || '';
      bVal = bVal?.toLowerCase?.() || '';
    }

    if (sortConfig.direction === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination for sellers
  const totalFilteredPages = Math.ceil(sortedSellers.length / ITEMS_PER_PAGE);
  const paginatedSellers = sortedSellers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Get filtered rentals for expanded seller with pagination
  const getFilteredRentalsForSeller = (sellerId) => {
    const seller = groupedBySeller[sellerId];
    if (!seller) return [];

    return seller.rentals.filter(rental => {
      return selectedStatus === "all" || rental.status.toLowerCase() === selectedStatus.toLowerCase();
    });
  };

  // Get paginated products for a seller
  const getPaginatedProductsForSeller = (sellerId) => {
    const filteredRentals = getFilteredRentalsForSeller(sellerId);
    const currentProductsPage = productsPage[sellerId] || 1;
    const startIndex = (currentProductsPage - 1) * PRODUCTS_PER_PAGE;
    const endIndex = startIndex + PRODUCTS_PER_PAGE;

    return {
      products: filteredRentals.slice(startIndex, endIndex),
      currentPage: currentProductsPage,
      totalPages: Math.ceil(filteredRentals.length / PRODUCTS_PER_PAGE),
      totalProducts: filteredRentals.length
    };
  };

  // Handle products page change
  const handleProductsPageChange = (sellerId, page) => {
    setProductsPage(prev => ({
      ...prev,
      [sellerId]: page
    }));
  };

  // Update rental status
  const updateRentalStatus = async (rentalId, newStatus) => {
    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${rentalId}-${newStatus}`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await updateStatusMutation({
        variables: { rentalId, status: newStatus.toLowerCase() },
      });
      refetch();
    } catch (err) {
      alert(`Error updating status: ${err.message}`);
    } finally {
      const updatingIdsCopy = new Set(updatingIds);
      updatingIdsCopy.delete(`${rentalId}-${newStatus}`);
      setUpdatingIds(updatingIdsCopy);
    }
  };

  // Delete rental
  const deleteRental = async (rentalId) => {
    if (!window.confirm("Are you sure you want to delete this rental?")) return;

    try {
      await deleteRentalMutation({
        variables: { rentalId },
      });
      refetch();
    } catch (err) {
      alert(`Error deleting rental: ${err.message}`);
    }
  };

  // Save edited rental
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingRental) return;

    try {
      await updateRentalMutation({
        variables: {
          rentalId: editingRental.rentalId,
          name: editingRental.name,
          brand: editingRental.brand,
          location: editingRental.location,
          pricePerHour: parseFloat(editingRental.pricePerHour),
          pricePerDay: parseFloat(editingRental.pricePerDay),
          quantity: parseInt(editingRental.quantity),
          description: editingRental.description,
          insurance: Boolean(editingRental.insurance),
          with_pilot: Boolean(editingRental.with_pilot),
          available_today: Boolean(editingRental.available_today)
        },
      });
      setEditingRental(null);
      refetch();
    } catch (err) {
      alert(`Error updating rental: ${err.message}`);
    }
  };

  // View rental details with actions
  const viewRentalDetails = (rental) => {
    setViewingRental(rental);
  };

  // Close rental details modal
  const closeRentalDetails = () => {
    setViewingRental(null);
  };

  // View description only
  const viewDescription = (rental) => {
    setViewingDescription(rental);
  };

  // Close description modal
  const closeDescription = () => {
    setViewingDescription(null);
  };



  // Handle seller page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (tableRef.current) {
      tableRef.current.scrollLeft = 0;
    }
  };

  const statusStats = {
    all: rentals.length,
    approved: rentals.filter(r => r.status === "approved").length,
    pending: rentals.filter(r => r.status === "pending").length,
    rejected: rentals.filter(r => r.status === "rejected").length,
    temporarily_deactivated: rentals.filter(r => r.status === "Temporarily_Deactivated").length,
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return <span style={{ marginLeft: '4px', fontSize: '12px' }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>;
  };

  const truncateDescription = (description, length = 30) => {
    if (!description) return "No description";
    if (description.length <= length) return description;
    return description.substring(0, length) + "...";
  };



  // Calculate overall stats
  const totalValue = rentals.reduce((sum, r) => sum + (r.pricePerHour * r.quantity), 0);
  const totalStock = rentals.reduce((sum, r) => sum + r.quantity, 0);
  const pendingRentals = rentals.filter(r => r.status === 'pending').length;
  const deactivatedRentals = rentals.filter(r => r.status === 'Temporarily_Deactivated').length;
  const insuredCount = rentals.filter(r => r.insurance).length;
  const withPilotCount = rentals.filter(r => r.with_pilot).length;
  const availableTodayCount = rentals.filter(r => r.available_today).length;

  return (
    <div className="drones-container rentals-container">
      <div className="main-wrapper">
        {/* Top Navigation */}
        <div className="top-navigation">
          <div className="nav-left">
            <h1>Drone Rentals Management System</h1>
            <p>Admin Dashboard • Manage Rental Products • Real-time Updates</p>
          </div>
          <div className="nav-right">
            <button
              className="btn btn-secondary"
              onClick={() => refetch()}
              disabled={loading}
            >
              {loading ? "🔄 Refreshing..." : "🔄 Refresh"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const headers = ["Rental ID", "Seller ID", "Name", "Brand", "Location", "Price/Hour", "Price/Day", "Quantity", "Status", "Insurance", "With Pilot", "Available Today", "Description"];
                const rows = rentals.map(rental => [
                  rental.rentalId,
                  rental.sellerId,
                  rental.name,
                  rental.brand,
                  rental.location,
                  rental.pricePerHour,
                  rental.pricePerDay,
                  rental.quantity,
                  rental.status,
                  rental.insurance ? "Yes" : "No",
                  rental.with_pilot ? "Yes" : "No",
                  rental.available_today ? "Yes" : "No",
                  rental.description
                ]);

                let csvContent = headers.join(',') + '\n';
                rows.forEach(row => {
                  csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
                });

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `rentals_export_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              📊 Export Report
            </button>
          </div>
        </div>

        {/* Plain Stats Dashboard */}
        <div className="compact-stats-container">
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Rental Value</div>
              <div className="compact-stat-value">₹{totalValue.toLocaleString('en-IN')}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Drones for Rent</div>
              <div className="compact-stat-value">{totalStock}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Sellers</div>
              <div className="compact-stat-value">{sellerGroups.length}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Pending Approval</div>
              <div className="compact-stat-value">{pendingRentals}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Deactivated</div>
              <div className="compact-stat-value">{deactivatedRentals}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Insured Rentals</div>
              <div className="compact-stat-value">{insuredCount}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">With Pilot</div>
              <div className="compact-stat-value">{withPilotCount}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Available Today</div>
              <div className="compact-stat-value">{availableTodayCount}</div>
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="control-panel">
          <div className="panel-header">
            <h2 className="panel-title">Rental Sellers Management</h2>
          </div>

          <div className="seller-selector">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search sellers by ID, email, or phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />
             
            </div>

            <div className="status-tabs-container">
              {["all", "approved", "pending", "rejected", "temporarily_deactivated"].map((status) => (
                <button
                  key={status}
                  className={`status-tab ${selectedStatus === status ? "active" : ""}`}
                  onClick={() => {
                    setSelectedStatus(status);
                    setCurrentPage(1);
                    // Reset product pages when status changes
                    setProductsPage({});
                  }}
                >
                  {status === "temporarily_deactivated" ? "Deactivated" : status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="tab-count">{statusStats[status]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => refetch()}>✕</button>
          </div>
        )}

        {/* Main Content Area */}
        <div className="content-area">
          {/* Sellers Table Container */}
          <div className="table-container">
            <div className="table-header">
              <h3 className="table-title">
                All Rental Sellers ({sellerGroups.length})
              </h3>
              <div className="table-controls">
                <div className="table-info">
                  Page {currentPage} of {totalFilteredPages || 1} • Showing {paginatedSellers.length} sellers
                </div>
              </div>
            </div>

            <div className="table-content" ref={tableRef}>
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading seller data...</p>
                </div>
              ) : sellerGroups.length === 0 ? (
                <div className="empty-state">
                  <p>No rental sellers found</p>
                </div>
              ) : (
                <table className="sellers-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("sellerId")}>
                        Seller ID <SortIcon column="sellerId" />
                      </th>
                      <th onClick={() => handleSort("email")}>
                        Contact Info <SortIcon column="email" />
                      </th>
                      <th onClick={() => handleSort("totalRentals")}>
                        Rentals <SortIcon column="totalRentals" />
                      </th>
                      <th onClick={() => handleSort("totalValue")}>
                        Rental Value <SortIcon column="totalValue" />
                      </th>
                      <th onClick={() => handleSort("totalStock")}>
                        Total Drones <SortIcon column="totalStock" />
                      </th>
                      {/* <th>Features</th> */}
                      <th>Status Breakdown</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSellers.map((seller) => {
                      const { products, currentPage: productsCurrentPage, totalPages: productsTotalPages, totalProducts } = getPaginatedProductsForSeller(seller.sellerId);

                      return (
                        <React.Fragment key={seller.sellerId}>
                          <tr
                            className={`seller-row ${expandedSeller === seller.sellerId ? 'expanded' : ''}`}
                            onClick={() => setExpandedSeller(
                              expandedSeller === seller.sellerId ? null : seller.sellerId
                            )}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="seller-id-cell">
                                <span className="expand-icon">
                                  {expandedSeller === seller.sellerId ? '▼' : '▶'}
                                </span>
                                <strong>Seller #{seller.sellerId}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-contact-cell">
                                <div className="seller-email">
                                  📧 {seller.sellerInfo?.email || "No email"}
                                </div>
                                <div className="seller-phone">
                                  📱 {seller.sellerInfo?.phoneNumber || "No phone"}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="seller-products-count">
                                <span className="count-badge">{seller.totalRentals}</span>
                                <span className="count-label">rentals</span>
                              </div>
                            </td>
                            <td>
                              <div className="seller-value">
                                <strong className="value-amount">
                                  ₹{seller.totalValue.toLocaleString('en-IN')}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-stock">
                                <span className="stock-count">{seller.totalStock}</span>
                                <span className="stock-label">drones</span>
                              </div>
                            </td>

                            <td>
                              <div className="status-breakdown">
                                <span className="status-dot approved" title={`${seller.approvedRentals} approved`}>
                                  {seller.approvedRentals}
                                </span>
                                <span className="status-dot pending" title={`${seller.pendingRentals} pending`}>
                                  {seller.pendingRentals}
                                </span>
                                <span className="status-dot rejected" title={`${seller.rejectedRentals} rejected`}>
                                  {seller.rejectedRentals}
                                </span>
                                <span className="status-dot deactivated" title={`${seller.deactivatedRentals} deactivated`}>
                                  {seller.deactivatedRentals}
                                </span>
                              </div>
                            </td>
                            <td>
                              <button
                                className="action-btn action-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedSeller(
                                    expandedSeller === seller.sellerId ? null : seller.sellerId
                                  );
                                }}
                                title="View Rentals"
                              >
                                {expandedSeller === seller.sellerId ? '👁️‍🗨️' : '👁️'}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Rentals Row */}
                          {expandedSeller === seller.sellerId && (
                            <tr className="products-row">
                              <td colSpan="8">
                                <div className="products-container">
                                  <div className="products-header">
                                    <div>
                                      <h4>Rentals from Seller #{seller.sellerId}</h4>
                                      <span className="products-count">
                                        {totalProducts} rentals • Showing {products.length} on page {productsCurrentPage} of {productsTotalPages}
                                      </span>
                                    </div>
                                  </div>

                                  {totalProducts === 0 ? (
                                    <div className="no-products">
                                      <p>No rentals found for the selected status filter.</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="products-table-container">
                                        <table className="products-table">
                                          <thead>
                                            <tr>
                                              <th>Rental ID</th>
                                              <th>Rental Details</th>
                                              <th>Brand</th>
                                              <th>Location</th>
                                              <th>Price Rates</th>
                                              <th>Stock</th>

                                              <th>Status</th>
                                              <th>Description</th>
                                              <th>Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {products.map((rental) => (
                                              <tr key={rental.rentalId}>
                                                <td>
                                                  <div className="id-cell">
                                                    {rental.rentalId}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-details-cell">
                                                    {rental.image && (
                                                      <img src={rental.image} alt={rental.name} className="drone-image" />
                                                    )}
                                                    <div>
                                                      <div className="drone-name">{rental.name}</div>
                                                      <div className="drone-specs">
                                                        <span className="drone-brand">{rental.brand}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-brand">{rental.brand}</div>
                                                </td>
                                                <td>
                                                  <div className="location-cell">
                                                    <span className="location-icon">📍</span>
                                                    {rental.location || "N/A"}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="price-cell">
                                                    <div className="hourly-rate">₹{rental.pricePerHour}/hr</div>
                                                    <div className="daily-rate">₹{rental.pricePerDay}/day</div>
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className={`quantity-cell ${rental.quantity === 0 ? 'out-of-stock' : ''}`}>
                                                    {rental.quantity}
                                                    {rental.quantity < 10 && rental.quantity > 0 && (
                                                      <span className="low-stock-indicator">!</span>
                                                    )}
                                                  </div>
                                                </td>

                                                <td>
                                                  <span className={`status-badge status-${rental.status === 'Temporarily_Deactivated' ? 'deactivated' : rental.status}`}>
                                                    {rental.status === 'Temporarily_Deactivated' ? 'DEACTIVATED' : rental.status.toUpperCase()}
                                                  </span>
                                                </td>
                                                <td>
                                                  <div>
                                                    {truncateDescription(rental.description, 30)}
                                                    {rental.description && rental.description.length > 30 && (
                                                      <button
                                                        onClick={() => viewDescription(rental)}
                                                        className="read-more-btn"
                                                      >
                                                        Read more
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="action-buttons">
                                                    {rental.status === "pending" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "approved")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "rejected")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-Temporarily_Deactivated`)}
                                                          title="Temporarily Deactivate"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-Temporarily_Deactivated`) ? "..." : "⛔"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {rental.status === "approved" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "pending")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "rejected")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-Temporarily_Deactivated`)}
                                                          title="Temporarily Deactivate"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-Temporarily_Deactivated`) ? "..." : "⛔"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {rental.status === "rejected" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "pending")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "approved")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-Temporarily_Deactivated`)}
                                                          title="Temporarily Deactivate"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-Temporarily_Deactivated`) ? "..." : "⛔"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {rental.status === "Temporarily_Deactivated" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "pending")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "approved")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateRentalStatus(rental.rentalId, "rejected")}
                                                          disabled={updatingIds.has(`${rental.rentalId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${rental.rentalId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    <button
                                                      className="action-btn action-view"
                                                      onClick={() => viewRentalDetails(rental)}
                                                      title="View Details"
                                                    >
                                                      👁
                                                    </button>



                                                    <button
                                                      className="action-btn action-delete"
                                                      onClick={() => deleteRental(rental.rentalId)}
                                                      title="Delete"
                                                    >
                                                      🗑
                                                    </button>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>

                                      {/* Products Pagination */}
                                      {productsTotalPages > 1 && (
                                        <div className="products-pagination">
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, productsCurrentPage - 1)}
                                            disabled={productsCurrentPage === 1}
                                            className="pagination-btn"
                                          >
                                            &lt; Previous
                                          </button>
                                          <div className="pagination-info">
                                            Page {productsCurrentPage} of {productsTotalPages}
                                          </div>
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, productsCurrentPage + 1)}
                                            disabled={productsCurrentPage === productsTotalPages}
                                            className="pagination-btn"
                                          >
                                            Next &gt;
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls for Sellers */}
            {totalFilteredPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalFilteredPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    className={currentPage === num ? "active" : ""}
                    onClick={() => handlePageChange(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalFilteredPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rental Details Modal with Actions */}
      {viewingRental && (
        <div className="modal-overlay" onClick={closeRentalDetails}>
          <div className="modal-content drone-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rental Details</h2>
              <button className="close-btn" onClick={closeRentalDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="drone-details-grid">
                <div className="drone-image-section">
                  {viewingRental.image ? (
                    <img
                      src={viewingRental.image}
                      alt={viewingRental.name}
                      className="drone-detail-image"
                    />
                  ) : (
                    <div className="no-image-placeholder">No Image</div>
                  )}
                </div>

                <div className="drone-info-section">
                  <div className="info-row">
                    <div className="info-label">Rental ID</div>
                    <div className="info-value">{viewingRental.rentalId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Name</div>
                    <div className="info-value">{viewingRental.name}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Brand</div>
                    <div className="info-value">{viewingRental.brand}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Location</div>
                    <div className="info-value">
                      <span className="location-icon">📍</span>
                      {viewingRental.location || "N/A"}
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Price Rates</div>
                    <div className="info-value price-value">
                      <div>₹{viewingRental.pricePerHour}/hour</div>
                      <div>₹{viewingRental.pricePerDay}/day</div>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Quantity</div>
                    <div className="info-value">
                      <span className={`quantity-display ${viewingRental.quantity === 0 ? 'out-of-stock' : ''}`}>
                        {viewingRental.quantity}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Status</div>
                    <div className="info-value">
                      <span className={`status-badge status-${viewingRental.status === 'Temporarily_Deactivated' ? 'deactivated' : viewingRental.status}`}>
                        {viewingRental.status === 'Temporarily_Deactivated' ? 'DEACTIVATED' : viewingRental.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller ID</div>
                    <div className="info-value">{viewingRental.sellerId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Email</div>
                    <div className="info-value">{viewingRental.sellerInfo?.email || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Phone</div>
                    <div className="info-value">{viewingRental.sellerInfo?.phoneNumber || "N/A"}</div>
                  </div>
                </div>

                <div className="drone-description-section">
                  <div className="info-label">Description</div>
                  <div className="description-content">
                    {viewingRental.description || 'No description available'}
                  </div>
                </div>

                {/* Actions in Details Modal */}
                <div className="drone-actions-section">
                  <div className="info-label">Quick Actions</div>
                  <div className="action-buttons-grid">
                    {viewingRental.status === "pending" && (
                      <>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "approved");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "rejected");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "Temporarily_Deactivated");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-Temporarily_Deactivated`)}
                          title="Temporarily Deactivate"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-Temporarily_Deactivated`) ? "..." : "⛔ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingRental.status === "approved" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "pending");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "rejected");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "Temporarily_Deactivated");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-Temporarily_Deactivated`)}
                          title="Temporarily Deactivate"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-Temporarily_Deactivated`) ? "..." : "⛔ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingRental.status === "rejected" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "pending");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "approved");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "Temporarily_Deactivated");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-Temporarily_Deactivated`)}
                          title="Temporarily Deactivate"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-Temporarily_Deactivated`) ? "..." : "⛔ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingRental.status === "Temporarily_Deactivated" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "pending");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "approved");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateRentalStatus(viewingRental.rentalId, "rejected");
                            closeRentalDetails();
                          }}
                          disabled={updatingIds.has(`${viewingRental.rentalId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingRental.rentalId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    <button
                      className="action-btn-large action-delete"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this rental?")) {
                          deleteRental(viewingRental.rentalId);
                          closeRentalDetails();
                        }
                      }}
                      title="Delete"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {viewingDescription && (
        <div className="modal-overlay" onClick={closeDescription}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Description</h2>
              <button className="close-btn" onClick={closeDescription}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
                {viewingDescription.description}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingRental && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Rental</h2>
              <button className="close-btn" onClick={() => setEditingRental(null)}>×</button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="modal-body">
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Name</label>
                  <input
                    type="text"
                    value={editingRental.name}
                    onChange={(e) => setEditingRental({ ...editingRental, name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Brand</label>
                  <input
                    type="text"
                    value={editingRental.brand}
                    onChange={(e) => setEditingRental({ ...editingRental, brand: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Location</label>
                  <input
                    type="text"
                    value={editingRental.location}
                    onChange={(e) => setEditingRental({ ...editingRental, location: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px', display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Price/Hour</label>
                    <input
                      type="number"
                      value={editingRental.pricePerHour}
                      onChange={(e) => setEditingRental({ ...editingRental, pricePerHour: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e5e5e5',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Price/Day</label>
                    <input
                      type="number"
                      value={editingRental.pricePerDay}
                      onChange={(e) => setEditingRental({ ...editingRental, pricePerDay: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e5e5e5',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Quantity</label>
                  <input
                    type="number"
                    value={editingRental.quantity}
                    onChange={(e) => setEditingRental({ ...editingRental, quantity: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Features</label>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={editingRental.insurance || false}
                        onChange={(e) => setEditingRental({ ...editingRental, insurance: e.target.checked })}
                      />
                      <span>🛡️ Insured</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={editingRental.with_pilot || false}
                        onChange={(e) => setEditingRental({ ...editingRental, with_pilot: e.target.checked })}
                      />
                      <span>👨✈️ With Pilot</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={editingRental.available_today || false}
                        onChange={(e) => setEditingRental({ ...editingRental, available_today: e.target.checked })}
                      />
                      <span>✅ Available Today</span>
                    </label>
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Description</label>
                  <textarea
                    value={editingRental.description}
                    onChange={(e) => setEditingRental({ ...editingRental, description: e.target.value })}
                    rows="6"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
              <div style={{
                padding: '24px',
                borderTop: '1px solid #e5e5e5',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <button
                  type="button"
                  onClick={() => setEditingRental(null)}
                  style={{
                    padding: '10px 24px',
                    border: '1px solid #e5e5e5',
                    background: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 24px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Rentals;